import { useQuery } from '@tanstack/react-query';
import { ChangeApprovalsApi } from 'types/backend';
import { changeApprovals } from 'services/api';
import { EnvironmentApprovalSettingsListResponse } from 'types/backend';

export const APPROVAL_SETTINGS_QUERY_KEY: string[] = ['approval-settings'];
export interface ApprovalSettingsDataProps {
  approvalSettings: EnvironmentApprovalSettingsListResponse | undefined;
  isFetchingApprovalSettings: boolean;
  isLoadingApprovalSettings: boolean;
  refetchApprovalSettings?: () => Promise<
    EnvironmentApprovalSettingsListResponse | undefined
  >;
}
/**
 * Service to fetch environment approval settings.
 */
export const getApprovalSettingsQueryService = ({
  changeApprovals,
  projectId,
}: {
  changeApprovals: ChangeApprovalsApi;
  projectId: number;
}) => ({
  fetch: async (): Promise<EnvironmentApprovalSettingsListResponse> => {
    try {
      return await changeApprovals.listEnvironmentApprovalSettings({
        project_id: projectId,
      });
    } catch (error) {
      throw new Error(
        `Failed to fetch approval settings for Project ID: ${projectId}.`
      );
    }
  },
});

/**
 * Custom hook for fetching a list of approval settings for a project.
 * (Note: The hook to fetch or mutate a **single** approval setting for a
 * given project/environment is defined in useEnvironmentApprovalSettingsData.ts)
 *
 * @param projectId - The project ID
 * @returns {ApprovalSettingsDataProps}
 */
const useApprovalSettingsData = (
  projectId: number
): ApprovalSettingsDataProps => {
  const queryService = getApprovalSettingsQueryService({
    changeApprovals,
    projectId,
  });

  const approvalSettingsQueryKey = [
    ...APPROVAL_SETTINGS_QUERY_KEY,
    String(projectId),
  ];

  const {
    data: approvalSettings,
    isFetching: isFetchingApprovalSettings,
    isLoading: isLoadingApprovalSettings,
    refetch: refetchApprovalSettings,
  } = useQuery({
    queryFn: queryService.fetch,
    queryKey: approvalSettingsQueryKey,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
  });

  return {
    approvalSettings,
    isFetchingApprovalSettings,
    isLoadingApprovalSettings,
    refetchApprovalSettings,
  };
};

export default useApprovalSettingsData;
